import { useQuery } from "@tanstack/react-query";

import type { TicketAppointmentStateDto } from "@/api/types";
import { DateRange, formatDateRange } from "@/components/DateRange/DateRange";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { useTranslation } from "@/translations";

type TicketAppointmentCardProps = {
  ticketId: string;
};

// Which states carry no information worth surfacing on the ticket — the card renders nothing for
// these (and when the ticket has no appointment information).
const HIDDEN_STATES: TicketAppointmentStateDto[] = ["notStarted"];

export function TicketAppointmentCard({ ticketId }: TicketAppointmentCardProps): React.ReactNode {
  const { t, i18n } = useTranslation();
  const { data: status } = useQuery(useTicketQueries().appointment(ticketId));
  const selectAppointmentMutation = ticketMutations.useSelectAppointment(ticketId);
  const changeAppointmentMutation = ticketMutations.useChangeAppointment(ticketId);

  if (!status || (HIDDEN_STATES.includes(status.state) && !status.selectedAppointmentStart)) {
    return null;
  }

  const hasAppointment = !!status.selectedAppointmentStart && !!status.selectedAppointmentEnd;
  const isAwaitingSelection = status.state === "awaitingResidentSelection";
  const canChangeAppointment = hasAppointment && status.canChange;

  return (
    <Card data-testid="ticket-jansen-huybregts-card">
      <Inline gap="sm" justify="between" align="center" wrap={false}>
        <Inline gap="sm" align="center" wrap={false}>
          <Text variant="body-emphasis" overflow="line-clamp-one">
            {t("page.tickets.appointment.title")}
          </Text>
        </Inline>
        <Badge variant={stateToBadgeVariant(status.state)} data-testid="ticket-jansen-huybregts-status">
          {t(stateToLabelKey(status.state))}
        </Badge>
      </Inline>

      {hasAppointment && (
        <Stack gap="xs">
          <Text variant="caption" tone="secondary" overflow="normal">
            {t("page.tickets.appointment.appointment")}
          </Text>
          <Text as="div" variant="body-emphasis" overflow="normal" data-testid="ticket-jansen-huybregts-appointment">
            <DateRange
              start={status.selectedAppointmentStart!}
              end={status.selectedAppointmentEnd!}
              format="shortWithTime"
            />
          </Text>
        </Stack>
      )}

      {isAwaitingSelection && status.options.length > 0 && (
        <Stack gap="sm">
          <Text variant="caption" tone="secondary" overflow="normal">
            {t("page.tickets.appointment.select-appointment")}
          </Text>
          <Stack gap="sm">
            {status.options.map((option) => (
              <Button
                key={option.slotId}
                data-testid={`ticket-jansen-huybregts-option-${option.slotId}`}
                variant="soft"
                size="sm"
                disabled={!status.canSelect}
                isLoading={selectAppointmentMutation.isPending && selectAppointmentMutation.variables === option.slotId}
                onClick={() => selectAppointmentMutation.mutate(option.slotId)}
              >
                {formatDateRange(i18n, { start: option.start, end: option.end, format: "shortWithTime" })}
              </Button>
            ))}
          </Stack>
        </Stack>
      )}

      {canChangeAppointment && (
        <Button
          data-testid="ticket-jansen-huybregts-change-appointment"
          variant="soft"
          size="sm"
          isLoading={changeAppointmentMutation.isPending}
          onClick={() => changeAppointmentMutation.mutate()}
        >
          {t("page.tickets.appointment.change-appointment")}
        </Button>
      )}

      {status.state === "failed" && (
        <Notice
          data-testid="ticket-jansen-huybregts-failed"
          variant="danger"
          icon={<AlertTriangleIcon />}
          message={t("page.tickets.appointment.state.failed")}
        />
      )}

      {status.requiresManualFollowUp && status.state !== "failed" && (
        <Notice
          data-testid="ticket-jansen-huybregts-manual-follow-up"
          variant="warning"
          icon={<AlertTriangleIcon />}
          message={t("page.tickets.appointment.manual-follow-up")}
        />
      )}
    </Card>
  );
}

function stateToBadgeVariant(state: TicketAppointmentStateDto) {
  switch (state) {
    case "confirmed":
    case "appointmentChanged":
      return "success" as const;
    case "failed":
      return "error" as const;
    case "manualFollowUp":
      return "warning" as const;
    default:
      return "info" as const;
  }
}

function stateToLabelKey(state: TicketAppointmentStateDto) {
  switch (state) {
    case "submitted":
      return "page.tickets.appointment.state.submitted" as const;
    case "fetchingAppointments":
      return "page.tickets.appointment.state.fetching-appointments" as const;
    case "awaitingResidentSelection":
      return "page.tickets.appointment.state.awaiting-resident-selection" as const;
    case "confirmed":
      return "page.tickets.appointment.state.confirmed" as const;
    case "appointmentChanged":
      return "page.tickets.appointment.state.appointment-changed" as const;
    case "manualFollowUp":
      return "page.tickets.appointment.state.manual-follow-up" as const;
    case "failed":
      return "page.tickets.appointment.state.failed" as const;
    case "notStarted":
      return "page.tickets.appointment.state.not-started" as const;
  }
}
